import { useState, useMemo } from 'react'
import { useFeatures, useDeleteFeature, type FeatureCategory } from '@/hooks/useAdminFeatures'
import FeatureEditor from '@/components/admin/FeatureEditor'

type CategoryFilter = 'all' | FeatureCategory

const CATEGORY_TABS = [
  { key: 'all' as const, label: 'All Features', color: 'gray' },
  { key: 'product' as const, label: 'Product', color: 'blue' },
  { key: 'portal' as const, label: 'Portal', color: 'teal' },
  { key: 'admin' as const, label: 'Admin', color: 'purple' },
  { key: 'distillation' as const, label: 'Distillation', color: 'amber' },
  { key: 'limit' as const, label: 'Limit', color: 'red' },
]

const CATEGORY_COLORS: Record<FeatureCategory, string> = {
  product: 'blue',
  portal: 'teal',
  admin: 'purple',
  distillation: 'amber',
  limit: 'red',
}

export default function Features() {
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [editingFeatureKey, setEditingFeatureKey] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [deletingFeatureKey, setDeletingFeatureKey] = useState<string | null>(null)

  const { data: features, isLoading, error } = useFeatures(
    categoryFilter === 'all' ? {} : { category: categoryFilter }
  )
  const deleteMutation = useDeleteFeature()

  // Filter features by search query
  const filteredFeatures = useMemo(() => {
    if (!features) return []

    if (!searchQuery.trim()) return features

    const query = searchQuery.toLowerCase()
    return features.filter(
      (f) =>
        f.key.toLowerCase().includes(query) ||
        f.name.toLowerCase().includes(query) ||
        f.description.toLowerCase().includes(query)
    )
  }, [features, searchQuery])

  const handleDelete = async (key: string) => {
    try {
      await deleteMutation.mutateAsync(key)
      setDeletingFeatureKey(null)
    } catch (error) {
      alert('Failed to delete feature: ' + (error as Error).message)
    }
  }

  return (
    <div className="min-h-screen bg-[#0a0e14] relative overflow-hidden">
      {/* Subtle scan-line effect */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.02]" style={{
        backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, #FFB84D 2px, #FFB84D 4px)'
      }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8 border-b border-[#FFB84D]/20 pb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <span className="text-[#FFB84D] text-3xl">⚙️</span>
              <h1 className="text-3xl font-bold text-[#FFB84D] tracking-wider uppercase" style={{
                fontFamily: '"Courier New", monospace',
                textShadow: '0 0 20px rgba(255, 184, 77, 0.3)',
                letterSpacing: '0.1em'
              }}>
                Feature Registry
              </h1>
            </div>
            <button
              onClick={() => setIsCreating(true)}
              className="px-5 py-2 bg-[#FFB84D] rounded
                         text-[#0a0e14] font-mono text-sm uppercase tracking-wider font-bold
                         hover:bg-[#FFB84D]/90 hover:shadow-[0_0_20px_rgba(255,184,77,0.3)]
                         transition-all duration-200"
            >
              + Create Feature
            </button>
          </div>
          <p className="text-[#8b949e] text-sm ml-11" style={{ fontFamily: '"Courier New", monospace' }}>
            CLASSIFIED // Feature Definition Management System
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by key, name, or description..."
            className="w-full bg-[#141c24] border border-[#2a3542] rounded px-4 py-3
                       text-[#e8eaed] font-mono text-sm
                       focus:outline-none focus:border-[#FFB84D]/50
                       focus:shadow-[0_0_15px_rgba(255,184,77,0.1)]
                       placeholder-[#8b949e]/50"
          />
        </div>

        {/* Category Filter Tabs */}
        <div className="mb-6">
          <div className="flex gap-2 overflow-x-auto">
            {CATEGORY_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setCategoryFilter(tab.key)}
                className={`
                  relative px-5 py-2 font-mono text-xs uppercase tracking-wider transition-all duration-200
                  whitespace-nowrap
                  ${categoryFilter === tab.key
                    ? 'bg-[#FFB84D]/10 text-[#FFB84D] border-l-2 border-[#FFB84D] shadow-[0_0_15px_rgba(255,184,77,0.15)]'
                    : 'text-[#8b949e] hover:text-[#FFB84D]/70 hover:bg-[#FFB84D]/5'
                  }
                `}
                style={{
                  clipPath: 'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Feature List */}
        {isLoading && (
          <div className="flex items-center justify-center h-96">
            <div className="text-[#FFB84D] font-mono animate-pulse">
              [LOADING FEATURE REGISTRY...]
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-900/20 border border-red-500/30 rounded p-4 font-mono text-red-400">
            ERROR: Failed to load features
          </div>
        )}

        {!isLoading && !error && (
          <>
            {filteredFeatures.length === 0 ? (
              <div className="bg-[#141c24]/80 border border-[#2a3542] rounded p-8 text-center">
                <div className="text-[#8b949e] font-mono text-sm">
                  {searchQuery ? 'No features match your search' : 'No features defined yet'}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredFeatures.map((feature) => (
                  <div
                    key={feature.key}
                    className="bg-[#141c24]/80 border border-[#2a3542] hover:border-[#FFB84D]/50
                               rounded-sm p-4 transition-all duration-200 group
                               hover:shadow-[0_0_20px_rgba(255,184,77,0.1)]"
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Feature Info */}
                      <div className="flex-1 min-w-0">
                        {/* Key and Category */}
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <span className="text-xs font-mono text-[#8b949e] uppercase tracking-wider">
                            [{feature.key}]
                          </span>
                          <CategoryBadge category={feature.category} />
                          {feature.is_premium && (
                            <span className="px-2 py-0.5 rounded text-xs font-mono bg-yellow-500/10 text-yellow-400 border border-yellow-500/30">
                              PREMIUM
                            </span>
                          )}
                          {feature.attestation_framework && feature.attestation_framework !== 'none' && (
                            <span className="px-2 py-0.5 rounded text-xs font-mono bg-green-500/10 text-green-400 border border-green-500/30">
                              🔒 {feature.attestation_framework}
                            </span>
                          )}
                        </div>

                        {/* Name */}
                        <div className="text-[#e8eaed] font-semibold mb-1 group-hover:text-[#FFB84D] transition-colors">
                          {feature.name}
                        </div>

                        {/* Description */}
                        <div className="text-sm text-[#8b949e] mb-2">
                          {feature.description}
                        </div>

                        {/* Plan Requirements */}
                        <div className="flex items-center gap-2 flex-wrap">
                          {feature.requires_trial && (
                            <span className="px-2 py-0.5 rounded text-xs font-mono bg-blue-500/10 text-blue-400 border border-blue-500/30">
                              Trial
                            </span>
                          )}
                          {feature.requires_pro && (
                            <span className="px-2 py-0.5 rounded text-xs font-mono bg-purple-500/10 text-purple-400 border border-purple-500/30">
                              Pro
                            </span>
                          )}
                          {feature.requires_enterprise && (
                            <span className="px-2 py-0.5 rounded text-xs font-mono bg-amber-500/10 text-amber-400 border border-amber-500/30">
                              Enterprise
                            </span>
                          )}
                          {!feature.requires_trial && !feature.requires_pro && !feature.requires_enterprise && (
                            <span className="px-2 py-0.5 rounded text-xs font-mono bg-gray-500/10 text-gray-400 border border-gray-500/30">
                              No plan required
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setEditingFeatureKey(feature.key)}
                          className="px-3 py-1.5 bg-[#2a3542] rounded text-xs font-mono uppercase
                                     text-[#8b949e] hover:text-[#FFB84D] hover:bg-[#FFB84D]/10
                                     transition-all duration-200"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeletingFeatureKey(feature.key)}
                          className="px-3 py-1.5 bg-[#2a3542] rounded text-xs font-mono uppercase
                                     text-[#8b949e] hover:text-red-400 hover:bg-red-500/10
                                     transition-all duration-200"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Count */}
            {filteredFeatures.length > 0 && (
              <div className="mt-6 text-center text-xs text-[#8b949e] font-mono">
                Showing {filteredFeatures.length} {filteredFeatures.length === 1 ? 'feature' : 'features'}
                {searchQuery && ` matching "${searchQuery}"`}
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(isCreating || editingFeatureKey) && (
        <FeatureEditor
          featureKey={editingFeatureKey}
          onClose={() => {
            setIsCreating(false)
            setEditingFeatureKey(null)
          }}
          onSuccess={() => {
            setIsCreating(false)
            setEditingFeatureKey(null)
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingFeatureKey && (
        <>
          <div
            className="fixed inset-0 bg-black/70 z-50"
            onClick={() => setDeletingFeatureKey(null)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="bg-[#0a0e14] border border-red-500/50 rounded-lg shadow-[0_0_50px_rgba(239,68,68,0.2)]
                         w-full max-w-md p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start gap-3 mb-4">
                <span className="text-2xl">⚠️</span>
                <div>
                  <h3 className="text-lg font-mono uppercase tracking-wider text-red-400 mb-2">
                    Delete Feature
                  </h3>
                  <p className="text-sm text-[#8b949e] font-mono">
                    Are you sure you want to delete feature <strong className="text-[#e8eaed]">{deletingFeatureKey}</strong>?
                  </p>
                  <p className="text-sm text-[#8b949e] font-mono mt-2">
                    This will remove the feature definition from the registry. Any existing feature overrides will remain but may no longer work.
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#2a3542]">
                <button
                  onClick={() => setDeletingFeatureKey(null)}
                  className="px-4 py-2 bg-[#141c24] border border-[#2a3542] rounded
                             text-[#8b949e] font-mono text-sm uppercase tracking-wider
                             hover:border-[#FFB84D]/30 hover:text-[#FFB84D]
                             transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deletingFeatureKey)}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 bg-red-500 rounded
                             text-white font-mono text-sm uppercase tracking-wider font-bold
                             hover:bg-red-600 hover:shadow-[0_0_20px_rgba(239,68,68,0.3)]
                             disabled:opacity-50 disabled:cursor-not-allowed
                             transition-all duration-200"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

function CategoryBadge({ category }: { category: FeatureCategory }) {
  const color = CATEGORY_COLORS[category]
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    teal: 'bg-teal-500/10 text-teal-400 border-teal-500/30',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
    amber: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    red: 'bg-red-500/10 text-red-400 border-red-500/30',
    gray: 'bg-gray-500/10 text-gray-400 border-gray-500/30',
  }

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-mono border ${colorClasses[color] || colorClasses.gray}`}>
      {category.toUpperCase()}
    </span>
  )
}
